import { CodeBlock, Note } from "./shared";

function Endpoint({
  method,
  path,
  description,
  auth,
  body,
  response,
  curl,
}: {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  auth?: boolean;
  body?: string;
  response: string;
  curl?: string;
}) {
  const methodColors: Record<string, string> = {
    GET: "bg-green-500/10 text-green-400 ring-green-500/20",
    POST: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
    PUT: "bg-amber/10 text-amber ring-amber/20",
    DELETE: "bg-red-500/10 text-red-400 ring-red-500/20",
  };

  return (
    <div className="rounded-xl border border-elevated bg-surface/30 p-6">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <span
          className={`rounded-md px-2.5 py-1 font-mono text-xs font-bold ring-1 ${methodColors[method]}`}
        >
          {method}
        </span>
        <code className="font-mono text-sm text-primary">{path}</code>
        {auth && (
          <span className="rounded-md bg-purple-500/10 px-2 py-0.5 font-mono text-xs text-purple-400 ring-1 ring-purple-500/20">
            Auth Required
          </span>
        )}
      </div>
      <p className="mb-4 text-sm text-secondary">{description}</p>
      {body && (
        <div className="mb-4">
          <p className="mb-2 font-mono text-xs font-semibold uppercase text-muted">Request Body</p>
          <CodeBlock code={body} language="json" />
        </div>
      )}
      <div className="mb-4">
        <p className="mb-2 font-mono text-xs font-semibold uppercase text-muted">Response</p>
        <CodeBlock code={response} language="json" />
      </div>
      {curl && (
        <div>
          <p className="mb-2 font-mono text-xs font-semibold uppercase text-muted">Example</p>
          <CodeBlock code={curl} language="bash" />
        </div>
      )}
    </div>
  );
}

export function AgentApiDocs({ apiUrl }: { apiUrl: string }) {
  return (
    <>
      {/* Quick Start */}
      <section id="agent-quick-start" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Quick Start</h2>
        <p className="mt-4 text-lg text-secondary">
          This documentation is designed for AI agents. For the full machine-readable version, fetch{" "}
          <code className="font-mono text-sm text-amber">GET /api/agent-docs</code> — it returns
          everything as plain text/markdown.
        </p>

        <div className="mt-6">
          <Note>
            Agents can fetch <code className="font-mono text-xs">{apiUrl}/api/agent-docs</code> to
            get the complete API documentation as parseable markdown in a single request.
          </Note>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Base URL</h3>
        <div className="mt-2">
          <CodeBlock code={apiUrl} language="text" />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Response Format</h3>
        <p className="mt-2 text-secondary">Every endpoint returns:</p>
        <div className="mt-3">
          <CodeBlock
            code={`// Success
{
  "success": true,
  "data": { ... }
}

// Error
{
  "success": false,
  "error": "Human-readable error message"
}`}
            language="json"
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Authentication</h3>
        <p className="mt-2 text-secondary">
          Get a JWT token via <code className="font-mono text-sm text-amber">POST /auth/login</code>
          , then pass it as a Bearer token:
        </p>
        <div className="mt-3">
          <CodeBlock
            code={`# Get token
TOKEN=$(curl -s -X POST ${apiUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "password"}' \\
  | jq -r '.data.token')

# Use token
curl -s ${apiUrl}/api/plugins \\
  -H "Authorization: Bearer $TOKEN"`}
            language="bash"
          />
        </div>
      </section>

      {/* Auth Endpoints */}
      <section id="agent-auth" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Auth Endpoints</h2>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="POST"
            path="/auth/login"
            description="Log in with email and password. Returns JWT token valid for 7 days."
            body={`{
  "email": "user@example.com",
  "password": "your-password"
}`}
            response={`{
  "success": true,
  "data": {
    "user": { "id": "clx...", "email": "user@example.com", "name": "User" },
    "token": "eyJhbGciOiJIUzI1NiIs..."
  }
}`}
            curl={`curl -s -X POST ${apiUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "your-password"}'`}
          />

          <Endpoint
            method="POST"
            path="/auth/signup"
            description="Create a new account. Returns user info and JWT token."
            body={`{
  "email": "newuser@example.com",
  "password": "secure-password",
  "name": "New User"
}`}
            response={`{
  "success": true,
  "data": {
    "user": { "id": "clx...", "email": "newuser@example.com", "name": "New User" },
    "token": "eyJhbGciOiJIUzI1NiIs..."
  }
}`}
          />

          <Endpoint
            method="GET"
            path="/auth/me"
            description="Get the currently authenticated user."
            auth
            response={`{
  "success": true,
  "data": {
    "user": { "userId": "clx...", "email": "user@example.com", "isAdmin": false }
  }
}`}
          />

          <Endpoint
            method="POST"
            path="/auth/reset-password"
            description="Reset a user's password. Requires X-Admin-Secret header."
            body={`{
  "email": "user@example.com",
  "newPassword": "new-secure-password"
}`}
            response={`{ "success": true }`}
            curl={`curl -s -X POST ${apiUrl}/auth/reset-password \\
  -H "Content-Type: application/json" \\
  -H "X-Admin-Secret: YOUR_ADMIN_SECRET" \\
  -d '{"email": "user@example.com", "newPassword": "new-secure-password"}'`}
          />
        </div>
      </section>

      {/* Public Plugin Endpoints */}
      <section id="agent-plugins-public" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugins — Public</h2>
        <p className="mt-4 text-secondary">These endpoints don't require authentication.</p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/api/plugins"
            description="List all published plugins with full metadata, files, and counts."
            response={`{
  "success": true,
  "data": [
    {
      "id": "clx...",
      "name": "code-simplifier",
      "slug": "code-simplifier",
      "description": "Simplify complex code",
      "tags": ["developer", "refactoring"],
      "author": { "id": "clx...", "name": "Author" },
      "files": [{ "fileName": "skills/simplify/SKILL.md", "fileType": "SKILL" }],
      "_count": { "downloads": 42, "reviews": 3, "favorites": 7 }
    }
  ]
}`}
            curl={`curl -s ${apiUrl}/api/plugins | jq '.data[] | {id, name, slug}'`}
          />

          <Endpoint
            method="GET"
            path="/api/plugins/:slug"
            description="Get a plugin manifest (name, description, version, author, file list)."
            response={`{
  "name": "code-simplifier",
  "description": "Simplify complex code",
  "version": "1.0.0",
  "author": { "name": "Author" },
  "files": ["skills/simplify/SKILL.md", "README.md"]
}`}
            curl={`curl -s ${apiUrl}/api/plugins/code-simplifier`}
          />

          <Endpoint
            method="GET"
            path="/api/marketplace.json"
            description="Full marketplace listing in Claude Code CLI format. CORS: *."
            response={`{
  "name": "supercharge-claude-code",
  "owner": { "name": "supercharge-claude-code" },
  "metadata": { "description": "Community plugins for Claude Code", "version": "1.0.0" },
  "plugins": [{ "name": "plugin-slug", "source": { "source": "url", "url": "..." } }]
}`}
            curl={`# Install full marketplace in Claude Code:
claude plugin marketplace add ${apiUrl}/api/marketplace.json`}
          />
        </div>
      </section>

      {/* Import */}
      <section id="agent-import" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugin Import</h2>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="POST"
            path="/plugins/import-url"
            description="Import a plugin from GitHub. Trusted publishers get auto-approved. Accepts GitHub URLs, subdirectory URLs, and skills.sh URLs. Auto-detects plugin structure — repos with just SKILL.md work."
            auth
            body={`{
  "url": "https://github.com/owner/repo"
}`}
            response={`{
  "success": true,
  "data": {
    "plugin": { "id": "clx...", "name": "repo-name", "slug": "repo-name", "status": "PUBLISHED" },
    "filesImported": 15
  }
}`}
            curl={`curl -s -X POST ${apiUrl}/plugins/import-url \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://github.com/owner/repo"}'`}
          />
        </div>
      </section>

      {/* Authenticated Plugin Endpoints */}
      <section id="agent-plugins-auth" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugins — Authenticated</h2>
        <p className="mt-4 text-secondary">
          Plugin management endpoints. All require{" "}
          <code className="font-mono text-sm text-amber">Authorization: Bearer &lt;token&gt;</code>.
        </p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/plugins/my-plugins"
            description="Get plugins owned by the authenticated user."
            auth
            response={`{
  "success": true,
  "data": [
    { "id": "clx...", "name": "my-plugin", "slug": "my-plugin", "status": "DRAFT" }
  ]
}`}
          />

          <Endpoint
            method="POST"
            path="/plugins"
            description="Create a new plugin draft."
            auth
            body={`{
  "name": "my-new-plugin",
  "description": "What this plugin does"
}`}
            response={`{
  "success": true,
  "data": { "id": "clx...", "name": "my-new-plugin", "slug": "my-new-plugin", "status": "DRAFT" }
}`}
          />

          <Endpoint
            method="POST"
            path="/plugins/:pluginId/files"
            description="Upload a file to a plugin. Uses multipart/form-data with 'file' and optional 'relativePath' fields."
            auth
            response={`{
  "success": true,
  "data": { "id": "clx...", "fileName": "skills/my-skill/SKILL.md", "fileType": "SKILL" }
}`}
            curl={`curl -X POST ${apiUrl}/plugins/PLUGIN_ID/files \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -F "file=@SKILL.md" \\
  -F "relativePath=skills/my-skill/SKILL.md"`}
          />

          <Endpoint
            method="POST"
            path="/plugins/:pluginId/submit"
            description="Submit a draft plugin for review. Trusted publishers are auto-approved."
            auth
            response={`{ "success": true, "data": { "id": "clx...", "status": "PENDING_REVIEW" } }`}
          />

          <Endpoint
            method="POST"
            path="/plugins/:pluginId/fork"
            description="Fork an existing plugin to create your own copy."
            auth
            response={`{
  "success": true,
  "data": { "id": "clx...", "name": "original-plugin-fork", "forkedFromId": "clx..." }
}`}
          />

          <Endpoint
            method="DELETE"
            path="/plugins/:pluginId"
            description="Delete an entire plugin."
            auth
            response={`{ "success": true, "data": { "message": "Plugin deleted" } }`}
          />
        </div>
      </section>

      {/* Favorites */}
      <section id="agent-favorites" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Favorites</h2>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/plugins/favorites/list"
            description="Get the authenticated user's favorited plugin IDs."
            auth
            response={`{ "success": true, "data": ["clx-plugin-id-1", "clx-plugin-id-2"] }`}
          />

          <Endpoint
            method="POST"
            path="/plugins/:pluginId/favorite"
            description="Toggle favorite status on a plugin."
            auth
            response={`{ "success": true, "data": { "favorited": true } }`}
          />
        </div>
      </section>

      {/* Marketplaces - Auth */}
      <section id="agent-marketplaces-auth" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">
          Marketplaces — Authenticated
        </h2>

        <div className="mt-4">
          <Note type="warning">
            Auth-protected marketplace endpoints use{" "}
            <code className="font-mono text-xs">/marketplaces/</code> (NO /api prefix). Public
            endpoints use <code className="font-mono text-xs">/api/marketplaces/</code>. Using the
            wrong prefix returns "Cannot POST/GET".
          </Note>
        </div>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/marketplaces"
            description="List marketplaces owned by the authenticated user."
            auth
            response={`{
  "success": true,
  "data": [
    {
      "id": "clx...",
      "name": "My Dev Tools",
      "slug": "my-dev-tools",
      "description": "Curated developer plugins",
      "_count": { "plugins": 3 }
    }
  ]
}`}
            curl={`curl -s ${apiUrl}/marketplaces \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />

          <Endpoint
            method="POST"
            path="/marketplaces"
            description="Create a new marketplace. Slug is auto-generated from name."
            auth
            body={`{
  "name": "My Dev Tools",
  "description": "Optional description"
}`}
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "name": "My Dev Tools",
    "slug": "my-dev-tools",
    "_count": { "plugins": 0 }
  }
}`}
            curl={`curl -s -X POST ${apiUrl}/marketplaces \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "My Dev Tools", "description": "Curated developer plugins"}'`}
          />

          <Endpoint
            method="PUT"
            path="/marketplaces/:id"
            description="Update marketplace name and/or description. Slug is regenerated from name."
            auth
            body={`{
  "name": "Updated Name",
  "description": "Updated description"
}`}
            response={`{ "success": true, "data": { "id": "clx...", "name": "Updated Name", "slug": "updated-name" } }`}
          />

          <Endpoint
            method="DELETE"
            path="/marketplaces/:id"
            description="Delete a marketplace and all its plugin selections. Owner only."
            auth
            response={`{ "success": true, "data": { "deleted": true } }`}
          />

          <Endpoint
            method="POST"
            path="/marketplaces/:id/plugins"
            description="Add a published plugin to a marketplace. Supports optional category and order. If the plugin already exists, its order and category are updated (upsert)."
            auth
            body={`{
  "pluginId": "clx...",
  "order": 0,
  "category": "Developer Tools"
}`}
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "marketplaceId": "clx...",
    "pluginId": "clx...",
    "order": 0,
    "category": "Developer Tools"
  }
}`}
            curl={`curl -s -X POST ${apiUrl}/marketplaces/MARKETPLACE_ID/plugins \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"pluginId": "PLUGIN_ID", "order": 0, "category": "Developer Tools"}'`}
          />

          <Endpoint
            method="DELETE"
            path="/marketplaces/:id/plugins/:pluginId"
            description="Remove a plugin from a marketplace."
            auth
            response={`{ "success": true, "data": { "removed": true } }`}
          />

          <Endpoint
            method="PUT"
            path="/marketplaces/:id/plugins/reorder"
            description="Reorder plugins within a marketplace."
            auth
            body={`{
  "plugins": [
    { "pluginId": "clx...", "order": 0 },
    { "pluginId": "clx...", "order": 1 }
  ]
}`}
            response={`{ "success": true, "data": { "reordered": true } }`}
          />
        </div>
      </section>

      {/* Marketplaces - Public */}
      <section id="agent-marketplaces-public" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Marketplaces — Public</h2>
        <p className="mt-4 text-secondary">No authentication required.</p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/api/marketplaces/:slug"
            description="Get a marketplace's public view with full plugin data."
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "name": "My Dev Tools",
    "slug": "my-dev-tools",
    "owner": { "id": "clx...", "name": "User" },
    "plugins": [
      {
        "id": "clx...",
        "name": "code-simplifier",
        "slug": "code-simplifier",
        "description": "Simplify complex code",
        "order": 0,
        "category": "Developer Tools"
      }
    ]
  }
}`}
            curl={`curl -s ${apiUrl}/api/marketplaces/my-dev-tools`}
          />

          <Endpoint
            method="GET"
            path="/api/marketplaces/:slug/marketplace.json"
            description="Claude Code CLI-compatible marketplace format. CORS: *."
            response={`{
  "name": "marketplace-slug",
  "owner": { "name": "Owner" },
  "metadata": { "description": "...", "version": "1.0.0" },
  "plugins": [{ "name": "plugin-slug", "source": { "source": "url", "url": "..." } }]
}`}
            curl={`# Install in Claude Code:
claude plugin marketplace add ${apiUrl}/api/marketplaces/supercharge-claude-code/marketplace.json`}
          />
        </div>
      </section>

      {/* Packs */}
      <section id="agent-packs" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Packs</h2>
        <p className="mt-4 text-secondary">
          Tag-based plugin groupings. No authentication required.
        </p>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="GET"
            path="/api/packs"
            description="List all packs with plugin counts."
            response={`{
  "success": true,
  "data": [
    {
      "id": "clx...",
      "name": "Developer Tools",
      "slug": "developer-tools",
      "tagFilter": "pack:developer-tools",
      "featured": true,
      "_count": { "plugins": 12 }
    }
  ]
}`}
            curl={`curl -s ${apiUrl}/api/packs`}
          />

          <Endpoint
            method="GET"
            path="/api/packs/:slug"
            description="Get pack detail with associated plugins."
            response={`{
  "success": true,
  "data": {
    "id": "clx...",
    "name": "Developer Tools",
    "slug": "developer-tools",
    "plugins": [
      { "id": "clx...", "name": "skill-creator", "slug": "skill-creator" }
    ]
  }
}`}
            curl={`curl -s ${apiUrl}/api/packs/developer-tools`}
          />
        </div>
      </section>

      {/* Skill Creator */}
      <section id="agent-skill-creator" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Skill Creator</h2>

        <div className="mt-8 space-y-6">
          <Endpoint
            method="POST"
            path="/api/skill-creator/chat"
            description="AI-powered skill generation. Rate limited: 10 req/min per IP. Send conversation messages and get AI responses."
            body={`{
  "messages": [
    { "role": "user", "content": "Create a skill that formats markdown tables" }
  ]
}`}
            response={`{
  "success": true,
  "data": {
    "role": "assistant",
    "content": "I'll help you create a markdown table formatting skill..."
  }
}`}
            curl={`curl -s -X POST ${apiUrl}/api/skill-creator/chat \\
  -H "Content-Type: application/json" \\
  -d '{"messages": [{"role": "user", "content": "Create a skill that formats markdown tables"}]}'`}
          />
        </div>
      </section>

      {/* End-to-End Workflow */}
      <section id="agent-workflow" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">End-to-End Workflow</h2>
        <p className="mt-4 text-secondary">
          Complete example: authenticate, import a plugin from GitHub, create a marketplace, add the
          plugin, and get the shareable link.
        </p>

        <div className="mt-6">
          <Note>
            For the full machine-readable documentation, fetch{" "}
            <code className="font-mono text-xs">{apiUrl}/api/agent-docs</code> — returns everything
            as plain text/markdown.
          </Note>
        </div>

        <div className="mt-6">
          <CodeBlock
            code={`# 1. Authenticate
TOKEN=$(curl -s -X POST ${apiUrl}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "password"}' \\
  | jq -r '.data.token')

# 2. Import a plugin from GitHub
IMPORT=$(curl -s -X POST ${apiUrl}/plugins/import-url \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://github.com/owner/repo"}')
PLUGIN_ID=$(echo $IMPORT | jq -r '.data.plugin.id')

# 3. Create a marketplace
MARKETPLACE=$(curl -s -X POST ${apiUrl}/marketplaces \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Agent Picks", "description": "AI-curated plugins"}')
MARKETPLACE_ID=$(echo $MARKETPLACE | jq -r '.data.id')
MARKETPLACE_SLUG=$(echo $MARKETPLACE | jq -r '.data.slug')

# 4. Add plugin to marketplace (with category)
curl -s -X POST ${apiUrl}/marketplaces/$MARKETPLACE_ID/plugins \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d "{\\"pluginId\\": \\"$PLUGIN_ID\\", \\"order\\": 0, \\"category\\": \\"Tools\\"}"

# 5. View public marketplace
curl -s ${apiUrl}/api/marketplaces/$MARKETPLACE_SLUG | jq

# 6. Install in Claude Code
echo "claude plugin marketplace add ${apiUrl}/api/marketplaces/$MARKETPLACE_SLUG/marketplace.json"`}
            language="bash"
          />
        </div>
      </section>
    </>
  );
}
